import { useState, useEffect, FormEvent } from "react";
import { LoginCredentials, UserSession } from "../../../domain/models";
import { useAuth } from "../../contexts";
import { useUseCases } from "../../contexts/DIContext";
import { databaseConfig } from "../../../config/database";
import { useOfflinePos } from "../../../offline/OfflinePosContext";
import { supportsOfflinePos } from "../../../runtime/frontofficeRuntime";
import {
  offlineProfileToUser,
  userToOfflineSnapshot,
} from "../../../offline/offlineAuthHelpers";
import { isLoginFormEnabled } from "./loginUiGates";
import { readTerminalSetup } from "./terminalSetup";
import type { OfflineBranch } from "@shared/offline";

// Validation patterns for cashier login
const usernameRegex = /^[a-zA-Z0-9_]{3,20}$/; // Alphanumeric and underscore, 3-20 characters
const passwordRegex = /^.{6,50}$/; // Minimum 6 characters, maximum 50

export interface Branch {
  id: string;
  visibleId: string;
  name: string;
  code: string;
  address: string;
  isMainBranch: boolean;
}

function mapOfflineBranch(b: OfflineBranch): Branch {
  return {
    id: b.visibleId,
    visibleId: b.visibleId,
    name: b.name,
    code: b.code,
    address: b.address ?? "",
    isMainBranch: b.isMainBranch === true,
  };
}

function mapApiBranch(b: {
  id?: string;
  visibleId?: string;
  name: string;
  code: string;
  address?: string;
  isMainBranch?: boolean;
}): Branch {
  return {
    id: b.id ?? b.visibleId ?? "",
    visibleId: b.visibleId ?? b.id ?? "",
    name: b.name,
    code: b.code,
    address: b.address ?? "",
    isMainBranch: b.isMainBranch === true,
  };
}

function selectDefaultBranchId(branchList: Branch[]): string {
  const mainBranch = branchList.find((b) => b.isMainBranch);
  if (mainBranch) return mainBranch.visibleId;
  return branchList[0]?.visibleId ?? "";
}

export const useLoginViewModel = () => {
  const { login } = useAuth();
  const { loginUseCase } = useUseCases();
  const offlinePos = useOfflinePos();
  const savedSetup = readTerminalSetup();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isLoadingBranches, setIsLoadingBranches] = useState<boolean>(!savedSetup);
  const [username, setUsername] = useState<string>("");
  const [password, setPassword] = useState<string>("");
  const [branchId, setBranchId] = useState<string>(savedSetup?.branchId ?? "");
  const [rememberMe, setRememberMe] = useState<boolean>(false);
  const [branches, setBranches] = useState<Branch[]>([]);
  const [errors, setErrors] = useState({
    username: "",
    password: "",
    branchId: "",
    response: ""
  });

  useEffect(() => {
    const fetchBranches = async () => {
      try {
        if (!savedSetup) setIsLoadingBranches(true);
        const baseUrl = databaseConfig.mongodb.apiBaseUrl;
        const response = await fetch(`${baseUrl}/api/branches`, {
          method: 'GET',
          headers: { 'Content-Type': 'application/json' },
        });

        if (response.ok) {
          const result = await response.json();
          const branchList: Branch[] = (result.data || []).map(mapApiBranch);
          setBranches(branchList);
          if (supportsOfflinePos()) {
            offlinePos.cacheBranches(
              branchList.map((b) => ({
                visibleId: b.visibleId,
                name: b.name,
                code: b.code,
                address: b.address,
                isMainBranch: b.isMainBranch,
              })),
            );
          }
          if (!savedSetup) {
            setBranchId(selectDefaultBranchId(branchList));
          }
          console.log(`✅ Loaded ${branchList.length} branches`);
        } else if (supportsOfflinePos()) {
          const cached = offlinePos.getCachedBranches().map(mapOfflineBranch);
          setBranches(cached);
          if (!savedSetup) setBranchId(selectDefaultBranchId(cached));
        } else {
          console.error('Failed to fetch branches:', response.statusText);
        }
      } catch (error) {
        if (supportsOfflinePos()) {
          const cached = offlinePos.getCachedBranches().map(mapOfflineBranch);
          setBranches(cached);
          if (!savedSetup) setBranchId(selectDefaultBranchId(cached));
        }
        console.error('Error fetching branches:', error);
      } finally {
        setIsLoadingBranches(false);
      }
    };

    void fetchBranches();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onUsernameChanged = (input: string) => {
    setUsername(input);

    if (!input.trim()) {
      setErrors(prev => ({
        ...prev,
        username: "Username is required",
        response: ""
      }));
    } else if (!usernameRegex.test(input)) {
      setErrors(prev => ({
        ...prev,
        username: "Username must be 3-20 characters long and can include letters, numbers, and underscores",
        response: ""
      }));
    } else {
      setErrors(prev => ({ ...prev, username: "", response: "" }));
    }
  };

  const onPasswordChanged = (input: string) => {
    setPassword(input);
    
    if (!input.trim()) {
      setErrors(prev => ({
        ...prev,
        password: "Password is required",
        response: ""
      }));
    } else if (!passwordRegex.test(input)) {
      setErrors(prev => ({
        ...prev,
        password: "Password must be at least 6 characters long",
        response: ""
      }));
    } else {
      setErrors(prev => ({ ...prev, password: "", response: "" }));
    }
  };

  const onBranchIdChanged = (input: string) => {
    setBranchId(input);
    
    if (!input.trim()) {
      setErrors(prev => ({
        ...prev,
        branchId: "Branch selection is required",
        response: ""
      }));
    } else {
      setErrors(prev => ({ ...prev, branchId: "", response: "" }));
    }
  };

  const onRememberMeChanged = (checked: boolean) => {
    setRememberMe(checked);
  };

  const onFormSubmitted = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    
    // Validate before submission
    let hasErrors = false;
    
    if (!username.trim()) {
      setErrors(prev => ({ ...prev, username: "Username is required" }));
      hasErrors = true;
    }
    
    if (!password.trim()) {
      setErrors(prev => ({ ...prev, password: "Password is required" }));
      hasErrors = true;
    }
    
    if (!branchId.trim()) {
      setErrors(prev => ({ ...prev, branchId: "Branch selection is required" }));
      hasErrors = true;
    }
    
    if (hasErrors || !isFormValid()) {
      return;
    }

    setIsLoading(true);
    
    try {
      const credentials: LoginCredentials = {
        username,
        password,
        branchId
      };

      const branch = branches.find((b) => b.visibleId === branchId);

      if (supportsOfflinePos() && !offlinePos.isOnline) {
        const result = await offlinePos.verifyOfflineLogin(credentials);
        if (!result.ok) {
          throw new Error(result.reason === 'Invalid credentials.' ? 'Invalid credentials' : result.reason);
        }
        const refreshToken = (await offlinePos.unsealProfileRefreshToken(result.profile)) ?? '';
        const userSession: UserSession = {
          user: offlineProfileToUser(result.profile),
          token: {
            accessToken: '',
            refreshToken,
            expiresAt: new Date(0),
          },
          branchId: credentials.branchId,
          branchName: branch?.name ?? result.profile.branchName ?? branchId,
          loginTime: new Date(),
          sessionMode: 'offline',
        };
        login(userSession, true);
        setErrors(prev => ({ ...prev, response: "Login successful! Redirecting..." }));
        return;
      }

      const userSession = await loginUseCase.execute(credentials);
      if (!userSession) {
        throw new Error('Invalid credentials');
      }

      login(
        { ...userSession, branchName: branch?.name ?? branchId },
        rememberMe,
      );

      if (supportsOfflinePos() && rememberMe) {
        await offlinePos.cacheAuthProfile({
          username: credentials.username,
          branchId: credentials.branchId,
          branchName: branch?.name,
          user: userToOfflineSnapshot(userSession.user),
          password: credentials.password,
          rememberMe: true,
          refreshToken: userSession.token.refreshToken,
        });
      }
      
      setErrors(prev => ({ ...prev, response: "Login successful! Redirecting..." }));
      
    } catch (error) {
      let errorMessage = "Something went wrong! Please try again later.";
      
      if (error instanceof Error) {
        if (error.message.includes("Invalid credentials")) {
          errorMessage = "Incorrect username or password!";
        } else if (error.message.includes("Branch not found")) {
          errorMessage = "Selected branch is not available!";
        } else if (
          supportsOfflinePos() &&
          (error.message.includes("remembered") || error.message.includes("No remembered"))
        ) {
          errorMessage = offlinePos.offlineLoginUnavailableMessage;
        } else if (error.message.length > 0 && error.message !== 'Something went wrong! Please try again later.') {
          errorMessage = error.message;
        }
      }
      
      setErrors(prev => ({
        ...prev,
        response: errorMessage
      }));
    } finally {
      setIsLoading(false);
    }
  };

  const onForgotPasswordClicked = () => {
    console.log('Forgot password clicked');
  };

  const isFormValid = (): boolean => {
    return (
      username.trim().length > 0 &&
      password.trim().length > 0 &&
      branchId.trim().length > 0 &&
      !errors.username &&
      !errors.password &&
      !errors.branchId
    );
  };

  const branchesAvailable = branches.length > 0;
  const loginEnabled = isLoginFormEnabled({
    canLoginOffline: offlinePos.canLoginOffline,
    firstLaunchBlocked: offlinePos.firstLaunchBlocked,
  });

  return {
    state: {
      username,
      password,
      branchId,
      rememberMe,
      isLoading,
      isLoadingBranches,
      branches,
      branchesAvailable,
      loginEnabled,
      errors,
      showBranchField: !savedSetup,
      terminalSetup: savedSetup,
    },
    intents: {
      onUsernameChanged,
      onPasswordChanged,
      onBranchIdChanged,
      onRememberMeChanged,
      onFormSubmitted,
      onForgotPasswordClicked,
      isFormValid
    }
  };
};
